import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/Card';
import { Calendar, Clock, MapPin, Search, LayoutGrid, List, ArrowRight } from 'lucide-react';
import { Skeleton } from '../components/ui/Skeleton';
import { Input } from '../components/ui/Input';
import { getEventos, getMinhasInscricoes, criarInscricao, getFullApiBaseUrl } from '../services/api';
import { Link } from 'react-router-dom';
import EventCarousel from '../components/EventCarousel';
import Pagination from '../components/Pagination'; // NOVO IMPORT

const ITEMS_PER_PAGE = 9; // 9 itens por página

function EventosPage() {
    const [allEvents, setAllEvents] = useState([]); // Todos os eventos carregados
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [minhasInscricoesIds, setMinhasInscricoesIds] = useState(new Set());
    const { isAuthenticated, participanteId, participanteRole } = useAuth();

    // Estados de Visualização e Paginação
    // Inicializa o estado lendo do localStorage, com 'grid' como fallback
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('eventosViewMode') || 'grid'); 
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Efeito para persistir o viewMode no localStorage
    useEffect(() => {
        localStorage.setItem('eventosViewMode', viewMode);
    }, [viewMode]);

    const fetchDados = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const eventosData = await getEventos(); // Busca todos os eventos
            
            // Ordena os eventos por data de início (crescente)
            const sortedEvents = eventosData.sort((a, b) => {
                return new Date(a.dataInicio) - new Date(b.dataInicio);
            });

            setAllEvents(sortedEvents); 

            if (isAuthenticated && participanteId) {
                const inscricoesData = await getMinhasInscricoes(participanteId);
                const ids = new Set(inscricoesData.map(inscricao => inscricao.evento.id));
                setMinhasInscricoesIds(ids);
            }
        } catch (err) {
            const errorMessage = 'Não foi possível carregar os dados dos eventos. Tente novamente mais tarde.';
            console.error('Erro ao buscar dados:', err);
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, participanteId]);

    useEffect(() => {
        fetchDados();
    }, [fetchDados]);

    const handleInscricao = async (eventoId) => {
        if (!isAuthenticated) {
            toast.warn('Você precisa estar logado para se inscrever em um evento.');
            return;
        }

        try {
            await criarInscricao(eventoId);
            toast.success('Inscrição realizada com sucesso!');
            setMinhasInscricoesIds(prevIds => new Set(prevIds).add(eventoId));
        } catch (err) {
            console.error('Erro ao inscrever-se:', err);
            toast.error(err.message || 'Ocorreu um erro ao tentar se inscrever.');
        }
    };

    // Lógica de Filtragem e Paginação
    const filteredAndSearchedEvents = allEvents.filter(evento =>
        evento.nome.toLowerCase().includes(searchQuery.toLowerCase()) ||
        evento.descricao.toLowerCase().includes(searchQuery.toLowerCase())
    );

    useEffect(() => {
        const newTotalPages = Math.ceil(filteredAndSearchedEvents.length / ITEMS_PER_PAGE);
        setTotalPages(newTotalPages > 0 ? newTotalPages : 1);
        // Se a página atual for maior que o total de páginas, volta para a última página válida
        if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
        } else if (currentPage === 0 && newTotalPages > 0) {
            setCurrentPage(1);
        }
    }, [filteredAndSearchedEvents.length, currentPage]);

    const paginatedEvents = filteredAndSearchedEvents.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Rola para o topo ao mudar de página
    };

    const EventosSkeleton = () => (
        <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
            {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                <Card key={index} className={viewMode === 'list' ? 'flex flex-col sm:flex-row' : 'flex flex-col'}>
                    <CardHeader className={viewMode === 'list' ? 'flex-shrink-0 w-full sm:w-48' : ''}>
                        <Skeleton className="h-6 w-3/4 mb-2" />
                        <Skeleton className="h-4 w-full mt-2" />
                    </CardHeader>
                    <CardContent className="flex-grow space-y-3">
                        <div className="flex items-center">
                            <Skeleton className="h-4 w-4 mr-2 rounded-full" />
                            <Skeleton className="h-4 w-2/3" />
                        </div>
                    </CardContent>
                    <CardFooter className={viewMode === 'list' ? 'sm:self-center sm:ml-auto' : ''}>
                        <Skeleton className="h-10 w-full sm:w-32" />
                    </CardFooter>
                </Card>
            ))}
        </div>
    );

    if (loading) {
        return (
            <div className="container mx-auto p-4 md:p-6 lg:p-8">
                <div className="mb-6">
                    <Skeleton className="h-8 w-1/3" />
                    <Skeleton className="h-4 w-1/2 mt-2" />
                </div>
                <EventosSkeleton />
            </div>
        );
    }

    if (error) {
        return <div className="container mx-auto p-4 text-destructive">{error}</div>;
    }

    // Função para obter o URL completo da imagem
    const getFullImageUrl = (url) => {
        if (!url) return null;
        const baseUrl = getFullApiBaseUrl();
        
        // Remove barras iniciais do URL do evento
        let normalizedUrl = url.startsWith('/') ? url.substring(1) : url;
        
        // Adiciona 'files/' se não estiver presente
        if (!normalizedUrl.startsWith('files/')) {
            normalizedUrl = `files/${normalizedUrl}`;
        }
        
        // Concatena: http://localhost:8080/api/ + files/ + nome_do_arquivo
        return `${baseUrl}/${normalizedUrl}`;
    };

    // Componente de Card de Evento (para Grid e Lista)
    const EventCard = ({ evento, isListMode }) => {
        const isSubscribed = minhasInscricoesIds.has(evento.id);
        const isEventoPassado = new Date(evento.dataFim) < new Date();
        const fullImageUrl = getFullImageUrl(evento.imagemUrl);
        const imageUrl = fullImageUrl || '/placeholder-i.jpg'; // USANDO PLACEHOLDER AQUI

        const baseClasses = "flex flex-col transition-all duration-200 ease-in-out hover:shadow-lg hover:-translate-y-1";
        const listClasses = "sm:flex-row sm:items-center sm:p-4 sm:space-x-4 hover:shadow-md hover:-translate-y-0";
        const gridClasses = "h-full";

        return (
            <Card key={evento.id} className={`${baseClasses} ${isListMode ? listClasses : gridClasses}`}>
                <Link to={`/eventos/${evento.id}`} className={`block h-full ${isListMode ? 'sm:flex sm:flex-grow sm:items-center' : 'flex flex-col'}`}>
                    <div className={`overflow-hidden ${isListMode ? 'w-full sm:w-40 sm:h-24 flex-shrink-0 rounded-md' : 'w-full aspect-3508/1665 rounded-t-lg'}`}>
                        <img 
                            src={imageUrl} // USANDO A VARIÁVEL imageUrl
                            alt={evento.nome} 
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className={`flex-grow ${isListMode ? 'p-0 sm:p-4' : 'p-6'}`}>
                        <CardTitle className={`leading-tight ${isListMode ? 'text-lg mb-1' : 'mb-2'}`}>{evento.nome}</CardTitle>
                        <CardDescription className={`leading-relaxed ${isListMode ? 'line-clamp-2 text-sm' : 'line-clamp-3'}`}>
                            {evento.descricao.replace(/<[^>]*>?/gm, '')}
                        </CardDescription>
                        <div className={`space-y-1 mt-3 ${isListMode ? 'hidden md:block' : 'block'}`}>
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Calendar className="mr-2 h-4 w-4" />
                                <span>{new Date(evento.dataInicio).toLocaleDateString()} - {new Date(evento.dataFim).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center text-sm text-muted-foreground">
                                <MapPin className="mr-2 h-4 w-4" />
                                <span>{evento.local}</span>
                            </div>
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Clock className="mr-2 h-4 w-4" />
                                <span>{evento.cargaHorariaTotal} horas</span>
                            </div>
                        </div>
                    </div>
                </Link>
                <CardFooter className={`pt-4 ${isListMode ? 'sm:pt-0 sm:pr-4 sm:pl-0 sm:self-center flex-shrink-0 w-full sm:w-auto' : 'w-full'}`}>
                    {evento.ativo ? (
                        isAuthenticated ? (
                            participanteRole === 'ROLE_ADMIN' ? (
                                <Link to={`/admin/eventos/${evento.id}/sessoes`} className="w-full">
                                    <Button variant="outline" className="w-full">Gerenciar Sessões <ArrowRight className="ml-2 h-4 w-4" /></Button>
                                </Link>
                            ) : isSubscribed ? (
                                <Button className="w-full bg-success text-success-foreground hover:bg-success/90" disabled>
                                    Inscrito
                                </Button>
                            ) : (
                                <Button className="w-full" onClick={(e) => { e.preventDefault(); handleInscricao(evento.id); }}>
                                    Inscrever-se
                                </Button>
                            )
                        ) : (
                            <Link to="/login" className="w-full">
                                <Button variant="outline" className="w-full">Faça login para se inscrever</Button>
                            </Link>
                        )
                    ) : (
                        <Button className="w-full" disabled>
                            Inscrições Inativas
                        </Button>
                    )}
                </CardFooter>
            </Card>
        );
    };

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            
            <EventCarousel events={allEvents} />

            <div className="mb-6">
                <h1 className="text-3xl font-bold tracking-tight">Todos os Eventos</h1>
                <p className="text-muted-foreground mb-4">Explore todos os eventos disponíveis e passados.</p>
                <div className="mt-4 flex flex-col md:flex-row gap-4 justify-between items-center">
                    <div className="relative flex-grow w-full md:w-auto">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Pesquisar eventos por nome ou descrição..."
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }} // Resetar página na pesquisa
                            className="pl-10 w-full"
                        />
                    </div>
                    
                    {/* Controles de Visualização */}
                    <div className="flex space-x-2 flex-shrink-0">
                        <Button 
                            variant={viewMode === 'grid' ? 'default' : 'outline'} 
                            size="icon" 
                            onClick={() => setViewMode('grid')}
                            aria-label="Visualização em Grelha"
                        >
                            <LayoutGrid className="h-5 w-5" />
                        </Button>
                        <Button 
                            variant={viewMode === 'list' ? 'default' : 'outline'} 
                            size="icon" 
                            onClick={() => setViewMode('list')}
                            aria-label="Visualização em Lista"
                        >
                            <List className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            </div>

            {paginatedEvents.length === 0 ? (
                 <Card className="text-center p-8 border-dashed">
                    <CardContent>
                        <p className="text-muted-foreground">
                            {searchQuery
                                ? `Nenhum evento encontrado com o seu critério de pesquisa na página ${currentPage}.`
                                : `Nenhum evento disponível no momento.`}
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                        {paginatedEvents.map(evento => (
                            <EventCard key={evento.id} evento={evento} isListMode={viewMode === 'list'} />
                        ))}
                    </div>
                    
                    <Pagination 
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </>
            )}
        </div>
    );
}

export default EventosPage;